// src/components/membership/MembershipPlans-Matrix.js
// =============================================================================
// MembershipPlansMatrix
// - Feature matrix / tier graph table view
// - Localized CSS for matrix-only UI
// =============================================================================

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Table } from "react-bootstrap";

import { MembershipPlansEmojiPicker } from "./MembershipPlans-EmojiPicker.js";
import { MembershipPlansPopupDialog } from "./MembershipPlans-PopupDialog.js";
import { FEATURE_ORDER, getAllFeaturesForPlanId, PLAN_ID } from "./MembershipPlans-SubscriptionStatus.js";
import "./MembershipPlans-Matrix.css";

const DIRECT_PRICING_EMAIL = "support@totdata.com";
const DIRECT_PRICING_MAILTO = `mailto:${DIRECT_PRICING_EMAIL}`;

function launchDirectPricingEmail() {
    if (typeof window === "undefined") return;
    window.location.assign(DIRECT_PRICING_MAILTO);
}

function normalizeYearUnit(s) {
    if (!s) return s;
    return String(s)
        .replaceAll("/Year", "/year")
        .replaceAll("/YEAR", "/year")
        .replaceAll("/Yr", "/year")
        .replaceAll("/YR", "/year")
        .replaceAll(" per year", "/year")
        .replaceAll(" Per year", "/year")
        .replaceAll(" Per Year", "/year")
        .trim();
}

function normalizePlanId(planId) {
    const normalized = String(planId || "").trim().toLowerCase().replace(/[_\s]+/g, "-");
    if (!normalized) return "";
    if (normalized === PLAN_ID.wildcatter) return PLAN_ID.wildcatter;
    if (normalized === PLAN_ID.blackgoldRevenue || normalized === "blackgoldrevenue") return PLAN_ID.blackgoldRevenue;
    if (normalized === PLAN_ID.blackgoldJib || normalized === "blackgoldjib") return PLAN_ID.blackgoldJib;
    if (normalized === PLAN_ID.oilBaron || normalized === "oilbaron") return PLAN_ID.oilBaron;
    if (normalized === PLAN_ID.bigRig || normalized === "bigrig") return PLAN_ID.bigRig;
    return normalized;
}

function priceForCta(plan, formatUSD) {
    if (!plan) return "";
    if (plan.price?.type === "fixed" && typeof plan.price.amount === "number") return `${formatUSD(plan.price.amount)}/year`;

    if (plan.price?.type === "custom") return plan.price.labelTop || "Custom pricing";
    if (plan.price?.type === "free") return plan.price.label || "FREE";

    return "";
}


export function MembershipPlansMatrix({
    plans,
    subscriptionStatus,
    highlightEnabled = false,
    onCheckoutPlanId,
    isPlanGreyedOut,
    currentPlanActionPlanId,
    text,
}) {
    const safeText = text || {};
    const hasStatus = Boolean(subscriptionStatus);
    const currentPlanId = useMemo(() => normalizePlanId(subscriptionStatus?.effectivePlanId || PLAN_ID.wildcatter), [subscriptionStatus?.effectivePlanId]);
    const actionPlanId = useMemo(() => normalizePlanId(currentPlanActionPlanId || ""), [currentPlanActionPlanId]);

    const hasFeature = useCallback((planId, featureLabel) => {
        return getAllFeaturesForPlanId(planId).includes(featureLabel);
    }, []);

    const hasBigRigColumn = useMemo(() => (plans || []).some((p) => normalizePlanId(p.id) === PLAN_ID.bigRig), [plans]);

    const featureRows = useMemo(() => {
        const all = new Set();
        (plans || []).forEach((p) => {
            getAllFeaturesForPlanId(p.id).forEach((f) => all.add(f));
        });

        const orderRank = new Map(FEATURE_ORDER.map((f, i) => [f, i]));
        return Array.from(all).sort((a, b) => {
            const ra = orderRank.has(a) ? orderRank.get(a) : 9999;
            const rb = orderRank.has(b) ? orderRank.get(b) : 9999;
            if (ra !== rb) return ra - rb;
            return a.localeCompare(b);
        });
    }, [plans]);


    const hint = safeText.tabAllPlansHint || "";
    const featureLabel = safeText.matrixFeature || "Feature";

    const formatUSD = useMemo(
        () => (n) =>
            new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
            }).format(n),
        []
    );

    const hasRevenue = Boolean(subscriptionStatus?.hasRevenue);
    const hasJib = Boolean(subscriptionStatus?.hasJib);
    const hasExactlyOneBlackGold = (hasRevenue && !hasJib) || (hasJib && !hasRevenue);

    const missingBlackGoldPlanId = hasExactlyOneBlackGold ? (hasRevenue ? PLAN_ID.blackgoldJib : PLAN_ID.blackgoldRevenue) : null;
    const missingBlackGoldLabel = hasExactlyOneBlackGold ? (hasRevenue ? "JIB" : "Revenue") : "";

    const missingBlackGoldPlan = useMemo(
        () => (missingBlackGoldPlanId ? (plans || []).find((p) => p.id === missingBlackGoldPlanId) : null),
        [plans, missingBlackGoldPlanId]
    );

    const missingBlackGoldPriceText = useMemo(() => {
        if (!missingBlackGoldPlan) return `${formatUSD(120)}/year`;
        return normalizeYearUnit(priceForCta(missingBlackGoldPlan, formatUSD));
    }, [missingBlackGoldPlan, formatUSD]);

    const isBlackGoldPeerPlan = useCallback(
        (planId) =>
            (currentPlanId === PLAN_ID.blackgoldRevenue && planId === PLAN_ID.blackgoldJib) ||
            (currentPlanId === PLAN_ID.blackgoldJib && planId === PLAN_ID.blackgoldRevenue),
        [currentPlanId]
    );

    const isPlanGreyedOutLocal = useCallback(
        (planId) => {
            if (isBlackGoldPeerPlan(planId)) return false;
            if (typeof isPlanGreyedOut === "function") return Boolean(isPlanGreyedOut(planId));
            return false;
        },
        [isPlanGreyedOut, isBlackGoldPeerPlan]
    );
    const [confirmAddOn, setConfirmAddOn] = useState(null); // { targetPlanId: string, requestedPlanId: string }
    const [showContactUsDialog, setShowContactUsDialog] = useState(false);

    const scrollRef = useRef(null);
    const currentHeaderRef = useRef(null);

    const [currentColMetrics, setCurrentColMetrics] = useState(null);
    const [currentColLeft, setCurrentColLeft] = useState(null);

    const HIGHLIGHT_PAD_PX = 0;

    const measureCurrentColumn = useCallback(() => {
        const scrollEl = scrollRef.current;
        const thEl = currentHeaderRef.current;

        if (!scrollEl || !thEl) {
            setCurrentColMetrics(null);
            setCurrentColLeft(null);
            return;
        }

        const scrollRect = scrollEl.getBoundingClientRect();
        const thRect = thEl.getBoundingClientRect();

        const contentLeft = thRect.left - scrollRect.left + scrollEl.scrollLeft;
        const width = thRect.width;

        setCurrentColMetrics({ contentLeft, width });
        setCurrentColLeft(contentLeft - scrollEl.scrollLeft);
    }, []);

    useLayoutEffect(() => {
        if (!highlightEnabled) {
            setCurrentColMetrics(null);
            setCurrentColLeft(null);
            return;
        }

        measureCurrentColumn();
    }, [highlightEnabled, measureCurrentColumn, plans, currentPlanId]);

    useEffect(() => {
        const onResize = () => measureCurrentColumn();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [measureCurrentColumn]);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl || !currentColMetrics) return;

        let rafId = 0;

        const onScroll = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                setCurrentColLeft(currentColMetrics.contentLeft - scrollEl.scrollLeft);
            });
        };

        scrollEl.addEventListener("scroll", onScroll, { passive: true });
        onScroll();

        return () => {
            scrollEl.removeEventListener("scroll", onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [currentColMetrics]);

    const currentColHighlightStyle = useMemo(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl || !currentColMetrics || typeof currentColLeft !== "number") return null;

        const desiredLeft = currentColLeft - HIGHLIGHT_PAD_PX;
        const desiredWidth = currentColMetrics.width + HIGHLIGHT_PAD_PX * 2;

        const maxVisibleRight = scrollEl.clientWidth;
        const clampedLeft = Math.max(0, desiredLeft);
        const clampedRight = Math.min(maxVisibleRight, desiredLeft + desiredWidth);
        const clampedWidth = Math.max(0, clampedRight - clampedLeft);

        return {
            left: clampedLeft,
            width: clampedWidth,
        };
    }, [currentColMetrics, currentColLeft]);

    const goToPayment = (planId) => {
        if (!planId) return;

        if (typeof onCheckoutPlanId === "function") {
            onCheckoutPlanId(planId);
            return;
        }

        window.location.assign("/payment");
    };

    const isCheckoutDisabled = (planId) => {
        if (!highlightEnabled) return true;
        if (!planId) return true;
        if (actionPlanId && planId === actionPlanId) return true;
        if (isPlanGreyedOutLocal(planId)) return true;

        const plan = (plans || []).find((p) => p.id === planId);
        if (!plan) return true;

        if (plan.price?.type === "custom") return false;
        return false;
    };

    const handlePlanCtaClick = (planId) => {
        if (isCheckoutDisabled(planId)) return;

        const plan = (plans || []).find((p) => p.id === planId);

        if (plan?.price?.type === "custom") {
            setShowContactUsDialog(true);
            return;
        }

        if (hasExactlyOneBlackGold && missingBlackGoldPlanId && (planId === PLAN_ID.oilBaron || planId === missingBlackGoldPlanId)) {
            setConfirmAddOn({ targetPlanId: missingBlackGoldPlanId, requestedPlanId: planId });
            return;
        }

        goToPayment(planId);
    };

    const closeConfirmAddOn = () => setConfirmAddOn(null);

    const proceedConfirmAddOn = () => {
        const targetPlanId = confirmAddOn?.targetPlanId;
        setConfirmAddOn(null);
        if (targetPlanId) goToPayment(targetPlanId);
    };

    const ctaLabelForPlan = (plan) => {
        if (!plan) return "";

        if (actionPlanId && plan.id === actionPlanId) return safeText.matrixCurrentPlanCta || "Current Plan";
        if (isPlanGreyedOutLocal(plan.id)) return safeText.matrixUnavailableCta || "Unavailable";
        if (plan.price?.type === "custom") return safeText.matrixCustomCta || "Contact us";

        if (hasExactlyOneBlackGold && missingBlackGoldPlanId && missingBlackGoldPriceText && (plan.id === PLAN_ID.oilBaron || plan.id === missingBlackGoldPlanId)) {
            return `Add ${missingBlackGoldLabel} for ${missingBlackGoldPriceText}`;
        }

        const priceText = priceForCta(plan, formatUSD);
        if (priceText) return `${safeText.matrixUpgradePrefix || "Upgrade for"} ${priceText}`;

        return safeText.matrixUpgradeFallback || "Upgrade";
    };

    if (!hasStatus && typeof process !== "undefined" && process.env?.NODE_ENV !== "production") { throw new Error("MembershipPlansMatrix requires subscriptionStatus"); }
    if (!hasStatus) return null;

    return (
        <div className="mc-matrix-wrap">
            <div className="mc-matrix-hint text-muted">{hint}</div>

            <div className="mc-matrix-shell">
                <div className="mc-matrix-scroll" ref={scrollRef}>
                    {highlightEnabled && currentColHighlightStyle ? (
                        <div className="mc-matrix-col-highlight" style={currentColHighlightStyle} aria-hidden="true" />
                    ) : null}

                    <Table bordered responsive className={`mc-matrix mb-0 ${hasBigRigColumn ? "has-big-rig" : ""}`.trim()}>
                        <thead>
                            <tr>
                                <th className="mc-matrix-feature-col">{featureLabel}</th>
                                {(plans || []).map((p) => {
                                    const greyed = isPlanGreyedOutLocal(p.id);
                                    return (
                                        <th
                                            key={p.id}
                                            ref={normalizePlanId(p.id) === currentPlanId ? currentHeaderRef : undefined}
                                            className={`mc-matrix-plan-col ${normalizePlanId(p.id) === currentPlanId ? "is-current" : ""} ${greyed ? "is-disabled" : ""}`}
                                        >
                                            <div className="mc-matrix-head">
                                                <div className="mc-matrix-plan-head">
                                                    <MembershipPlansEmojiPicker planId={p.id} isWildcatterCustom={Boolean(p?.id && PLAN_ID.wildcatter && p.id === PLAN_ID.wildcatter)} as="span" className="mc-icon mc-matrix-icon" emojiClassName="mc-plan-emoji" ariaHidden={true} />
                                                    <span className={`mc-matrix-plan${normalizePlanId(p.id) === currentPlanId ? " is-current-text" : ""}`}>{p.title}</span>
                                                    <span className={`mc-matrix-tier${(normalizePlanId(p.id) === PLAN_ID.wildcatter || normalizePlanId(p.id) === PLAN_ID.oilBaron) ? " is-gold" : ""}`}>{p.tier}</span>
                                                    {p.badge ? <span className="mc-matrix-badge">{p.badge.text}</span> : null}
                                                </div>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        <tbody>
                            {(featureRows || []).map((feat) => (
                                <tr key={feat}>
                                    <td className="mc-matrix-feature">{feat}</td>
                                    {(plans || []).map((p) => (
                                        <td
                                            key={`${p.id}:${feat}`}
                                            className={`mc-matrix-cell ${normalizePlanId(p.id) === currentPlanId ? "is-current" : ""} ${isPlanGreyedOutLocal(p.id) ? "is-disabled" : ""}`}
                                        >
                                            {hasFeature?.(p.id, feat)
                                                ? <span className="mc-check">&#10003;</span>
                                                : <span className="mc-cross" aria-hidden="true" />}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>

                        <tfoot>
                            <tr>
                                <td className="mc-matrix-cta-spacer" />
                                {(plans || []).map((p) => {
                                    const greyed = isPlanGreyedOutLocal(p.id);
                                    const disabled = isCheckoutDisabled(p.id);
                                    return (
                                        <td
                                            key={`${p.id}:cta`}
                                            className={`mc-matrix-cta-cell ${normalizePlanId(p.id) === currentPlanId ? "is-current" : ""} ${greyed ? "is-disabled" : ""}`}
                                        >
                                            {(() => {
                                                const isPaidCurrent = normalizePlanId(p.id) === actionPlanId;
                                                const isProPlan = normalizePlanId(p.id) === PLAN_ID.oilBaron;
                                                const btnClass = `mc-matrix-cta-btn${isPaidCurrent ? " is-current" : ""}${isProPlan && !isPaidCurrent ? " is-pro" : ""}`;
                                                return (
                                                    <button
                                                        type="button"
                                                        className={btnClass}
                                                        disabled={disabled}
                                                        onClick={() => handlePlanCtaClick(p.id)}
                                                        title={ctaLabelForPlan(p)}
                                                    >
                                                        {ctaLabelForPlan(p)}
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </div>

            <MembershipPlansPopupDialog open={Boolean(confirmAddOn)} subscriptionStatus={subscriptionStatus} request={confirmAddOn} onCancel={closeConfirmAddOn} onProceed={proceedConfirmAddOn} text={safeText} />

            {showContactUsDialog && (
                <div
                    className="mc-addon-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Contact support"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setShowContactUsDialog(false);
                    }}
                >
                    <div className="mc-addon-modal">
                        <div className="mc-addon-title">Contact Our Support Team</div>
                        <div className="mc-addon-text">
                            <div>Please email our support team to inquire about Big Rig pricing and get set up:</div>
                            <div style={{ marginTop: "0.5rem", fontWeight: 600 }}>{DIRECT_PRICING_EMAIL}</div>
                        </div>
                        <div className="mc-addon-actions">
                            <button type="button" className="mc-addon-btn danger" onClick={() => setShowContactUsDialog(false)}>
                                Close
                            </button>
                            <button
                                type="button"
                                className="mc-addon-btn primary"
                                onClick={() => {
                                    setShowContactUsDialog(false);
                                    launchDirectPricingEmail();
                                }}
                            >
                                Send Email
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 